<script setup>
import { ref, watch, onMounted, computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import aiReplyPreferenceAPI from 'dashboard/api/aiReplyPreference';

const props = defineProps({
  mode: { type: String, default: 'suggestion' },
  isSaving: { type: Boolean, default: false },
  embedded: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const instance = getCurrentInstance();
const isModeControlled = computed(() =>
  Object.prototype.hasOwnProperty.call(instance?.vnode?.props || {}, 'mode')
);

const selectedMode = ref(props.mode);
const autoGenerateSuggestion = ref(false);
const isSavingState = ref(false);

const isSavingValue = computed(() => props.isSaving || isSavingState.value);

watch(
  () => props.mode,
  value => {
    selectedMode.value = value;
  }
);

const toggleAutoReply = event => {
  selectedMode.value = event.target.checked ? 'auto_reply' : 'suggestion';
};

const handleSave = () => {
  if (isModeControlled.value) {
    emit('update', selectedMode.value);
    return;
  }

  isSavingState.value = true;
  aiReplyPreferenceAPI
    .update({
      mode: selectedMode.value,
      auto_generate_suggestion: autoGenerateSuggestion.value,
    })
    .then(() => {
      emit('update', selectedMode.value);
      useAlert(t('AI_REPLY_PREFERENCE.MESSAGES.SAVE_SUCCESS'));
    })
    .catch(error => {
      useAlert(
        parseAPIErrorResponse(error) ||
          t('AI_REPLY_PREFERENCE.MESSAGES.SAVE_FAILED')
      );
    })
    .finally(() => {
      isSavingState.value = false;
    });
};

onMounted(() => {
  if (isModeControlled.value) return;

  aiReplyPreferenceAPI
    .get()
    .then(({ data }) => {
      selectedMode.value = data.mode || 'suggestion';
      autoGenerateSuggestion.value = data.auto_generate_suggestion || false;
    })
    .catch(error => {
      useAlert(
        parseAPIErrorResponse(error) ||
          t('AI_REPLY_PREFERENCE.MESSAGES.LOAD_FAILED')
      );
    });
});
</script>

<template>
  <div
    class="grid gap-8"
    :class="{
      'py-16 px-5 font-inter mx-auto sm:max-w-screen-md': !embedded,
    }"
  >
    <div v-if="!embedded" class="flex flex-col gap-2">
      <h2 class="text-2xl font-medium text-n-slate-12">
        {{ $t('AI_REPLY_PREFERENCE.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('AI_REPLY_PREFERENCE.DESCRIPTION') }}
      </p>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4">
      <div class="flex items-center justify-between gap-4">
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('AI_REPLY_PREFERENCE.AUTO_REPLY.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('AI_REPLY_PREFERENCE.AUTO_REPLY.DESCRIPTION') }}
          </p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            :checked="selectedMode === 'auto_reply'"
            data-test-id="ai-reply-auto-reply-toggle"
            type="checkbox"
            class="sr-only peer"
            @change="toggleAutoReply"
          />
          <div
            class="w-11 h-6 bg-n-alpha-black2 rounded-full peer peer-checked:bg-n-brand transition"
          />
          <div
            class="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"
          />
        </label>
      </div>
    </div>

    <div
      v-if="selectedMode === 'suggestion'"
      class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4"
    >
      <div class="flex items-center justify-between gap-4">
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('AI_REPLY_PREFERENCE.AUTO_GENERATE_SUGGESTION.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('AI_REPLY_PREFERENCE.AUTO_GENERATE_SUGGESTION.DESCRIPTION') }}
          </p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            v-model="autoGenerateSuggestion"
            data-test-id="ai-reply-auto-generate-toggle"
            type="checkbox"
            class="sr-only peer"
          />
          <div
            class="w-11 h-6 bg-n-alpha-black2 rounded-full peer peer-checked:bg-n-brand transition"
          />
          <div
            class="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"
          />
        </label>
      </div>
    </div>

    <button
      class="reset-base inline-flex items-center justify-center rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition hover:bg-n-brand/90 disabled:opacity-60"
      data-test-id="ai-reply-preference-save"
      :disabled="isSavingValue"
      @click="handleSave"
    >
      {{ $t('AI_REPLY_PREFERENCE.ACTIONS.SAVE') }}
    </button>
  </div>
</template>
